// src/context/LocationContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { locationService } from '../services/locationService';
import { storage } from '../utils/storage';

// Initial state
const initialState = {
  userLocation: null,
  selectedLocation: null,
  isLocationEnabled: false,
  isLoadingLocation: false,
  locationError: null,
  nearbyHospitals: [],
  nearbyPharmacies: [],
  locationHistory: [],
  searchRadius: 5000, // 5km default
  coordinates: {
    latitude: null,
    longitude: null
  }
};

// Action types
const LocationActionTypes = {
  SET_USER_LOCATION: 'SET_USER_LOCATION',
  SET_SELECTED_LOCATION: 'SET_SELECTED_LOCATION',
  SET_LOCATION_LOADING: 'SET_LOCATION_LOADING',
  SET_LOCATION_ERROR: 'SET_LOCATION_ERROR',
  SET_LOCATION_ENABLED: 'SET_LOCATION_ENABLED',
  SET_NEARBY_HOSPITALS: 'SET_NEARBY_HOSPITALS',
  SET_NEARBY_PHARMACIES: 'SET_NEARBY_PHARMACIES',
  ADD_TO_LOCATION_HISTORY: 'ADD_TO_LOCATION_HISTORY',
  SET_SEARCH_RADIUS: 'SET_SEARCH_RADIUS',
  CLEAR_LOCATION_ERROR: 'CLEAR_LOCATION_ERROR'
};

// Reducer
const locationReducer = (state, action) => {
  switch (action.type) {
    case LocationActionTypes.SET_USER_LOCATION:
      return {
        ...state,
        userLocation: action.payload,
        coordinates: {
          latitude: action.payload?.latitude || null,
          longitude: action.payload?.longitude || null
        },
        isLoadingLocation: false,
        locationError: null
      };

    case LocationActionTypes.SET_SELECTED_LOCATION:
      return {
        ...state,
        selectedLocation: action.payload
      };

    case LocationActionTypes.SET_LOCATION_LOADING:
      return {
        ...state,
        isLoadingLocation: action.payload
      };

    case LocationActionTypes.SET_LOCATION_ERROR:
      return {
        ...state,
        locationError: action.payload,
        isLoadingLocation: false
      };

    case LocationActionTypes.SET_LOCATION_ENABLED:
      return {
        ...state,
        isLocationEnabled: action.payload
      };

    case LocationActionTypes.SET_NEARBY_HOSPITALS:
      return {
        ...state,
        nearbyHospitals: action.payload
      };

    case LocationActionTypes.SET_NEARBY_PHARMACIES:
      return {
        ...state,
        nearbyPharmacies: action.payload
      };

    case LocationActionTypes.ADD_TO_LOCATION_HISTORY:
      return {
        ...state,
        locationHistory: [action.payload, ...state.locationHistory.slice(0, 9)] // Keep last 10
      };

    case LocationActionTypes.SET_SEARCH_RADIUS:
      return {
        ...state,
        searchRadius: action.payload
      };

    case LocationActionTypes.CLEAR_LOCATION_ERROR:
      return {
        ...state,
        locationError: null
      };

    default:
      return state;
  }
};

// Create context
const LocationContext = createContext();

// Location provider component
export const LocationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(locationReducer, initialState);

  // Initialize location on app load
  useEffect(() => {
    const savedLocation = storage.getItem('userLocation');
    const savedRadius = storage.getItem('searchRadius');
    const locationHistory = storage.getItem('locationHistory') || [];

    if (savedLocation) {
      dispatch({
        type: LocationActionTypes.SET_USER_LOCATION,
        payload: savedLocation
      });
    }

    if (savedRadius) {
      dispatch({
        type: LocationActionTypes.SET_SEARCH_RADIUS,
        payload: parseInt(savedRadius)
      });
    }

    if (locationHistory.length > 0) {
      dispatch({
        type: LocationActionTypes.ADD_TO_LOCATION_HISTORY,
        payload: locationHistory[0]
      });
      locationHistory.slice(1).forEach(location => {
        dispatch({
          type: LocationActionTypes.ADD_TO_LOCATION_HISTORY,
          payload: location
        });
      });
    }

    // Check if geolocation is supported
    if ('geolocation' in navigator) {
      dispatch({ type: LocationActionTypes.SET_LOCATION_ENABLED, payload: true });
    }
  }, []);

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = 'Geolocation is not supported by this browser';
        dispatch({
          type: LocationActionTypes.SET_LOCATION_ERROR,
          payload: error
        });
        reject(new Error(error));
        return;
      }

      dispatch({ type: LocationActionTypes.SET_LOCATION_LOADING, payload: true });

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Get address from coordinates
            const addressData = await locationService.reverseGeocode(latitude, longitude);
            
            const locationData = {
              latitude,
              longitude,
              accuracy: position.coords.accuracy,
              address: addressData.address,
              city: addressData.city,
              country: addressData.country,
              timestamp: new Date().toISOString()
            };

            dispatch({
              type: LocationActionTypes.SET_USER_LOCATION,
              payload: locationData
            });

            // Save to storage
            storage.setItem('userLocation', locationData);

            // Add to history
            dispatch({
              type: LocationActionTypes.ADD_TO_LOCATION_HISTORY,
              payload: locationData
            });

            resolve(locationData);
          } catch (error) {
            dispatch({
              type: LocationActionTypes.SET_LOCATION_ERROR,
              payload: 'Failed to get address for location'
            });
            reject(error);
          }
        },
        (error) => {
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
            default:
              errorMessage = 'An unknown error occurred';
          }

          dispatch({
            type: LocationActionTypes.SET_LOCATION_ERROR,
            payload: errorMessage
          });
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  // Set location manually (for address search)
  const setLocation = async (address) => {
    try {
      dispatch({ type: LocationActionTypes.SET_LOCATION_LOADING, payload: true });

      const coordinatesData = await locationService.geocode(address);
      
      const locationData = {
        latitude: coordinatesData.latitude,
        longitude: coordinatesData.longitude,
        address: address,
        city: coordinatesData.city,
        country: coordinatesData.country,
        timestamp: new Date().toISOString(),
        manual: true
      };

      dispatch({
        type: LocationActionTypes.SET_USER_LOCATION,
        payload: locationData
      });

      // Save to storage
      storage.setItem('userLocation', locationData);

      // Add to history
      dispatch({
        type: LocationActionTypes.ADD_TO_LOCATION_HISTORY,
        payload: locationData
      });

      return locationData;
    } catch (error) {
      dispatch({
        type: LocationActionTypes.SET_LOCATION_ERROR,
        payload: 'Failed to find location for the given address'
      });
      throw error;
    }
  };

  // Find nearby hospitals
  const findNearbyHospitals = async (location = state.userLocation, radius = state.searchRadius) => {
    if (!location) {
      throw new Error('Location not available');
    }

    try {
      const hospitals = await locationService.findNearbyHospitals(
        location.latitude,
        location.longitude,
        radius
      );

      dispatch({
        type: LocationActionTypes.SET_NEARBY_HOSPITALS,
        payload: hospitals
      });

      return hospitals;
    } catch (error) {
      dispatch({
        type: LocationActionTypes.SET_LOCATION_ERROR,
        payload: 'Failed to find nearby hospitals'
      });
      throw error;
    }
  };

  // Find nearby pharmacies
  const findNearbyPharmacies = async (location = state.userLocation, radius = state.searchRadius) => {
    if (!location) {
      throw new Error('Location not available');
    }

    try {
      const pharmacies = await locationService.findNearbyPharmacies(
        location.latitude,
        location.longitude,
        radius
      );

      dispatch({
        type: LocationActionTypes.SET_NEARBY_PHARMACIES,
        payload: pharmacies
      });

      return pharmacies;
    } catch (error) {
      dispatch({
        type: LocationActionTypes.SET_LOCATION_ERROR,
        payload: 'Failed to find nearby pharmacies'
      });
      throw error;
    }
  };

  // Calculate distance between two points
  const calculateDistance = (point1, point2) => {
    return locationService.calculateDistance(
      point1.latitude,
      point1.longitude,
      point2.latitude,
      point2.longitude
    );
  };

  // Update search radius
  const updateSearchRadius = (radius) => {
    dispatch({
      type: LocationActionTypes.SET_SEARCH_RADIUS,
      payload: radius
    });
    storage.setItem('searchRadius', radius.toString());
  };

  // Clear location error
  const clearError = () => {
    dispatch({ type: LocationActionTypes.CLEAR_LOCATION_ERROR });
  };

  // Watch position (for real-time tracking)
  const watchPosition = (callback) => {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported');
    }

    return navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const locationData = {
          latitude,
          longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        callback(locationData);
      },
      (error) => {
        dispatch({
          type: LocationActionTypes.SET_LOCATION_ERROR,
          payload: 'Failed to track location'
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000 // 1 minute
      }
    );
  };

  const value = {
    ...state,
    getCurrentLocation,
    setLocation,
    findNearbyHospitals,
    findNearbyPharmacies,
    calculateDistance,
    updateSearchRadius,
    clearError,
    watchPosition
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;